import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, CalendarClock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export function RateRollover({ onDone }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({}); // item_code -> new rate string
  const [eff, setEff] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const start = async () => {
    setOpen(true); setLoading(true); setDraft({}); setEff(""); setConfirming(false);
    try {
      const d = await api.allRates();
      setItems(d.items);
      setDraft(Object.fromEntries(d.items.map((i) => [i.item_code, String(i.current_rate)])));
    } catch { toast.error("Could not load rates"); }
    finally { setLoading(false); }
  };

  const changes = items
    .filter((i) => draft[i.item_code] !== "" && draft[i.item_code] != null && Number(draft[i.item_code]) !== Number(i.current_rate))
    .map((i) => ({ ...i, new_rate: Number(draft[i.item_code]) }));

  const review = () => {
    if (!eff) return toast.error("Pick an effective date");
    if (changes.length === 0) return toast.error("No rates changed");
    setConfirming(true);
  };

  const apply = async () => {
    setBusy(true);
    try {
      const res = await api.rolloverRates({ effective_date: eff, auto_close: true,
        changes: changes.map((c) => ({ item_code: c.item_code, new_rate: c.new_rate })) });
      toast.success(`Applied ${res.count} rate change(s) from ${res.effective_date}`);
      setOpen(false);
      if (onDone) onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollover failed"); }
    finally { setBusy(false); }
  };

  // group items by item_group
  const groups = items.reduce((m, i) => { (m[i.item_group] = m[i.item_group] || []).push(i); return m; }, {});

  return (
    <>
      <Button size="sm" variant="outline" className="h-9 text-xs" onClick={start} data-testid="fy-rollover-open">
        <CalendarClock className="h-3.5 w-3.5 mr-1.5" />FY rate update
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="fy-rollover-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">Financial-year rate update</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
          ) : !confirming ? (
            <>
              <div className="flex items-end gap-3 pb-2 border-b border-slate-100">
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Effective from</label>
                  <Input type="date" value={eff} onChange={(e) => setEff(e.target.value)} className="h-9 text-xs mt-1 w-44" data-testid="fy-effective-date" />
                </div>
                <div className="text-[11px] text-slate-500 pb-2">Edit only the rates that changed. The previous rate is auto-closed the day before this date.</div>
              </div>
              <div className="overflow-y-auto flex-1 -mx-1 px-1">
                {Object.keys(groups).sort().map((g) => (
                  <div key={g} className="mt-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 sticky top-0 bg-white py-1">{g}</div>
                    {groups[g].map((i) => {
                      const changed = draft[i.item_code] !== "" && Number(draft[i.item_code]) !== Number(i.current_rate);
                      return (
                        <div key={i.item_code} className={`flex items-center gap-2 py-1.5 text-xs border-b border-slate-50 ${changed ? "bg-rose-50/50" : ""}`} data-testid={`fy-item-${i.item_code}`}>
                          <div className="flex-1 min-w-0">
                            <div className="truncate text-slate-800">{i.item_name}</div>
                            <div className="text-[10px] text-slate-400">{i.item_code} · {i.tier}</div>
                          </div>
                          <span className="text-slate-400 tabular-nums w-16 text-right">${Number(i.current_rate).toFixed(2)}</span>
                          <ArrowRight className="h-3 w-3 text-slate-300" />
                          <Input type="number" step="0.01" value={draft[i.item_code] ?? ""} onChange={(e) => setDraft((d) => ({ ...d, [i.item_code]: e.target.value }))}
                            className="h-8 text-xs w-24" data-testid={`fy-rate-${i.item_code}`} />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <DialogFooter className="border-t border-slate-100 pt-3">
                <span className="text-[11px] text-slate-500 mr-auto self-center" data-testid="fy-change-count">{changes.length} rate(s) changed</span>
                <Button variant="outline" size="sm" onClick={() => setOpen(false)}>Cancel</Button>
                <Button size="sm" onClick={review} data-testid="fy-review">Review changes</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <div className="overflow-y-auto flex-1">
                <div className="text-xs text-slate-600 mb-2">These {changes.length} change(s) will be applied, effective <b>{eff}</b>. Previous rates will be closed off on {eff}.</div>
                {changes.map((c) => (
                  <div key={c.item_code} className="flex items-center gap-2 py-1.5 text-xs border-b border-slate-50" data-testid={`fy-preview-${c.item_code}`}>
                    <div className="flex-1 min-w-0"><div className="truncate text-slate-800">{c.item_name}</div><div className="text-[10px] text-slate-400">{c.item_code}</div></div>
                    <span className="text-slate-400 tabular-nums">${Number(c.current_rate).toFixed(2)}</span>
                    <ArrowRight className="h-3 w-3 text-slate-300" />
                    <span className="font-semibold text-emerald-700 tabular-nums">${c.new_rate.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <DialogFooter className="border-t border-slate-100 pt-3">
                <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>Back</Button>
                <Button size="sm" onClick={apply} disabled={busy} data-testid="fy-apply" style={{ backgroundColor: "var(--brand)" }}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Apply ${changes.length} change(s)`}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
